/**
 * AbilityEvents - Ability System Socket Event Handlers
 *
 * Handles events related to abilities:
 * - abilityStateSync: Initial ability state from server
 * - abilityActivated: Ability was activated
 * - abilityDeactivated: Ability was deactivated
 * - abilityEffectEnded: Ability effect ended
 * - abilityCooldownUpdate: Cooldown update from server
 * - abilityActivationFailed: Activation failed
 * - abilityEquipSuccess: Ability equipped successfully
 * - abilityEquipFailed: Ability equip failed
 * - decoyRemoved: A decoy left the world (shot, replaced, owner died/left)
 * - invulnerabilityHit: Invulnerability blocked a hit
 * - damageBlocked: Damage was blocked
 */

import { abilityRegistry } from '../../../shared/AbilityRegistry.js';
import { resolveSound } from '../../../shared/resolveSound.js';
import { notifyAbilityActivated, notifyAbilityDeactivated } from '../../rendering/pixi/abilityEffects.js';
import { SPATIAL_AUDIO_CONFIG } from '../../audio/spatial-audio-config.js';
import { ClientLogger } from '../../logger/ClientLogger.js';

const abilityEventsLoggerFallback = (() => {
  if (typeof window !== 'undefined' && window.console) {
    return window.console;
  }
  return { log: () => {}, warn: () => {}, error: () => {}, info: () => {} };
})();
const abilityEventsLogger = ClientLogger.getLogger('ability') || abilityEventsLoggerFallback;

const AbilityEvents = {
  /**
   * Register all ability-related event handlers
   * @param {Socket} socket - Socket.io socket instance
   * @param {GameState} gameState - Game state container
   * @param {Object} callbacks - Callback functions
   * @param {Function} callbacks.showNotification - Show UI notification
   * @param {Function} callbacks.showEliminationNotification - Centre banner (kind 'decoy' when you shoot one)
   * @param {Function} callbacks.getTeleportTrailConfig - Get teleport trail config
   * @param {Function} callbacks.createFloatingDamageNumber - Create floating number
   */
  register(socket, gameState, callbacks) {
    const {
      showNotification,
      showEliminationNotification,
      getTeleportTrailConfig,
      createFloatingDamageNumber,
      spatialAudio = null
    } = callbacks;

    /**
     * The client helpers an ability's registry entry may need. Passed in so
     * abilityEffects.js stays free of client and renderer imports.
     */
    const abilityServices = () => ({
      teleportTrailDuration: getTeleportTrailConfig ? getTeleportTrailConfig().duration : undefined
    });

    function setPlayerAbilityActive(playerId, abilityId, isActive) {
      if (!playerId || !abilityId) {
        return null;
      }

      const player = gameState.players.get(playerId);
      if (!player) {
        return null;
      }

      player.activeAbilityEffects = player.activeAbilityEffects || {};
      if (isActive) {
        player.activeAbilityEffects[abilityId] = true;
      } else {
        delete player.activeAbilityEffects[abilityId];
      }

      return player;
    }

    function applySilenceEnd(gameState, serverEnd) {
      if (!serverEnd) {
        gameState.abilityState.silencedUntil = 0;
        return;
      }
      const remaining = serverEnd - Date.now();
      gameState.abilityState.silencedUntil = remaining > 0 ? performance.now() + remaining : 0;
    }

    function applyGlobalCooldownData(rawData, now, serverNow) {
      if (rawData === undefined || rawData === null) return;
      gameState.abilityState.globalCooldowns = gameState.abilityState.globalCooldowns || { utility: 0, movement: 0 };

      const updateCategory = (category, endTime) => {
        if (typeof endTime !== 'number') return;
        const remaining = endTime - serverNow;
        gameState.abilityState.globalCooldowns[category] = remaining > 0 ? now + remaining : 0;
      };

      if (typeof rawData === 'number') {
        const remaining = rawData - serverNow;
        const converted = remaining > 0 ? now + remaining : 0;
        gameState.abilityState.globalCooldowns.utility = converted;
        gameState.abilityState.globalCooldowns.movement = converted;
        return;
      }

      for (const [category, endTime] of Object.entries(rawData)) {
        updateCategory(category, endTime);
      }
    }

    // Initial ability state sync from server
    socket.on('abilityStateSync', (data) => {
      // Update global cooldown duration
      if (data.globalCooldown !== undefined) {
        gameState.abilityState.globalCooldownDuration = data.globalCooldown;
      }

      // Update equipped abilities
      if (data.state?.equipped) {
        gameState.abilityState.equipped = data.state.equipped;
      }

      if (data.state) {
        const now = performance.now();
        const serverNow = Date.now();

        // Sync cooldowns (convert server timestamps to client performance.now() relative)
        if (data.state.cooldowns) {
          gameState.abilityState.cooldowns = {};

          for (const [abilityId, endTime] of Object.entries(data.state.cooldowns)) {
            const remaining = endTime - serverNow;
            if (remaining > 0) {
              gameState.abilityState.cooldowns[abilityId] = now + remaining;
            }
          }
        }

        if (data.state.globalCooldowns || typeof data.state.globalCooldownEnd === 'number') {
          applyGlobalCooldownData(data.state.globalCooldowns ?? data.state.globalCooldownEnd, now, serverNow);
        }

        applySilenceEnd(gameState, data.state?.silencedUntil);
      }
    });

    socket.on('abilityNullified', (data) => {
      applySilenceEnd(gameState, data?.silencedUntil);
      showNotification?.('Abilities nullified', 'warning');
    });

    // Ability activated (by any player)
    socket.on('abilityActivated', (data) => {
      if (data.playerId === gameState.localId) {
        // Update local active effect tracking for duration display. This must
        // happen before the ability's own hook, which may annotate the entry.
        if (data.duration > 0) {
          const now = performance.now();
          gameState.abilityState.activeEffects[data.abilityId] = {
            startTime: now,
            endTime: now + data.duration
          };
        }
      }

      const player = setPlayerAbilityActive(data.playerId, data.abilityId, true);

      // Whatever else this particular ability needs set up lives in its own
      // registry entry (abilityEffects.js), not in a branch here.
      notifyAbilityActivated(data.abilityId, player, data, gameState, abilityServices());
    });

    // Ability deactivated (by any player)
    socket.on('abilityDeactivated', (data) => {
      // Clear the ability state tracking
      if (data.playerId === gameState.localId) {
        delete gameState.abilityState.activeEffects[data.abilityId];
        const duration = gameState.abilityState.globalCooldownDuration || 0;
        if (duration > 0) {
          const predictEnd = performance.now() + duration;
          gameState.abilityState.globalCooldowns = gameState.abilityState.globalCooldowns || { utility: 0, movement: 0 };
          gameState.abilityState.globalCooldowns.utility = predictEnd;
          gameState.abilityState.globalCooldowns.movement = predictEnd;
        }
      }

      const player = setPlayerAbilityActive(data.playerId, data.abilityId, false);
      notifyAbilityDeactivated(data.abilityId, player, data, gameState, abilityServices());
    });

    // Ability cancelled (e.g., rewind manual cancellation)
    socket.on('abilityCancelled', (data) => {
      // Clear ability state tracking and apply cooldowns
      if (data.playerId === gameState.localId) {
        delete gameState.abilityState.activeEffects[data.abilityId];

        const now = performance.now();
        const serverNow = Date.now();

        // Apply ability cooldown
        if (data.cooldownEnd) {
          const remaining = data.cooldownEnd - serverNow;
          if (remaining > 0) {
            gameState.abilityState.cooldowns[data.abilityId] = now + remaining;
          }
        }

        // Apply global cooldowns
        if (data.globalCooldowns) {
          applyGlobalCooldownData(data.globalCooldowns, now, serverNow);
        }
      }

      const player = setPlayerAbilityActive(data.playerId, data.abilityId, false);

      // A cancellation is a deactivation as far as the ability's own state is
      // concerned; `data.rewindData` and friends ride along for the entry that
      // knows what to do with them.
      notifyAbilityDeactivated(data.abilityId, player, { ...data, cancelled: true }, gameState, abilityServices());
    });

    // Local player's effect ended
    socket.on('abilityEffectEnded', (data) => {
      delete gameState.abilityState.activeEffects[data.abilityId];
      setPlayerAbilityActive(gameState.localId, data.abilityId, false);

      const now = performance.now();
      const serverNow = Date.now();

      if (data.globalCooldowns || data.globalCooldownEnd) {
        applyGlobalCooldownData(data.globalCooldowns ?? data.globalCooldownEnd, now, serverNow);
      }

    });

    // Server cooldown update (corrects client prediction if needed)
    socket.on('abilityCooldownUpdate', (data) => {
      const now = performance.now();
      const serverNow = Date.now();

      if (data.cooldownEnd) {
        const remaining = data.cooldownEnd - serverNow;
        if (remaining > 0) {
          gameState.abilityState.cooldowns[data.abilityId] = now + remaining;
        }
      }

      if (data.globalCooldowns || data.globalCooldownEnd) {
        applyGlobalCooldownData(data.globalCooldowns ?? data.globalCooldownEnd, now, serverNow);
      }
    });

    // Ability activation failed
    socket.on('abilityActivationFailed', (data) => {
      const reason = data?.reason || 'Unknown';
      if (showNotification) {
        showNotification(`Ability activation failed: ${reason}`, 'warning');
      }
    });

    // Ability equipped successfully
    socket.on('abilityEquipSuccess', (data) => {
      if (!data) return;
      const { abilityId, category, stacks } = data;
      if (!category || !abilityId) return;

      gameState.abilityState.equipped[category] = { abilityId, stacks: stacks || 1 };
      abilityEventsLogger.info(`✅ Server confirmed equip: ${abilityId} → ${category} (x${stacks || 1})`);
    });

    // Ability equip failed
    socket.on('abilityEquipFailed', (data) => {
      const reason = data?.reason || 'Unknown';
      abilityEventsLogger.warn(`❌ Equip failed: ${reason}`);
      if (showNotification) {
        showNotification(`Equip failed: ${reason}`, 'warning');
      }
    });

    // Player was healed
    socket.on('playerHealed', (data) => {
      if (!data || !data.playerId || !data.amount) return;

      const player = gameState.players.get(data.playerId);
      if (player && createFloatingDamageNumber) {
        createFloatingDamageNumber(
          data.playerId,
          data.amount,
          player.x,
          player.y,
          { type: 'heal' }
        );
      }
    });

    // A decoy left the world. Spawning rides the state snapshot; only removal
    // needs an event, because the moment it happens is what players must see.
    // The break sound comes through 'abilitySound' below, emitted by the server.
    // A shot decoy dies like a player (the renderer's death beat), so a bystander
    // reads a kill; only the shooter is told, with the bamboozled banner.
    socket.on('decoyRemoved', (data) => {
      if (!data || !data.id) return;
      gameState.decoys.delete(data.id);
      if (data.reason !== 'shot') return;   // replaced/ownerDied/ownerLeft: it just goes

      gameState.recordDecoyShot(data.id, performance.now());
      if (data.ownerId === gameState.localId) {
        showNotification?.('Your decoy was shot', 'info');
      } else if (data.shooterId === gameState.localId) {
        const ownerName = gameState.players.get(data.ownerId)?.name ?? null;
        showEliminationNotification?.(data.ownerId, ownerName, 'decoy');
      }
    });

    // Ability sound effect
    socket.on('abilitySound', (data) => {
      if (!data || !data.abilityId || !data.soundEvent) return;

      // Get ability config from registry
      const ability = typeof abilityRegistry !== 'undefined' ? abilityRegistry.get(data.abilityId) : null;
      if (!ability) {
        abilityEventsLogger.warn('[AbilityEvents] Ability not found in registry:', data.abilityId);
        return;
      }

      const resolved = resolveSound(ability.sounds?.[data.soundEvent]);
      if (!resolved) {
        abilityEventsLogger.warn(`[AbilityEvents] No sound configured for ${data.abilityId}:${data.soundEvent}`);
        return;
      }
      const { path: soundPath, volume } = resolved;

      // Use spatial audio if available and this is a room broadcast from another player
      if (spatialAudio && data.broadcastType === 'room' && data.playerId !== gameState.localId) {
        // Spatial audio for other players
        spatialAudio.playAt(soundPath, data.x, data.y, {
          config: SPATIAL_AUDIO_CONFIG.abilities,
          volume
        });
      } else if (spatialAudio) {
        // Local sound (no spatialization) for own abilities
        spatialAudio.playLocal(soundPath, { volume });
      } else {
        // Fallback to HTML5 Audio if spatial audio not available
        const audio = new Audio(soundPath);
        audio.volume = volume;
        audio.currentTime = 0;
        audio.play().catch(err => {
          abilityEventsLogger.error(`[AbilityEvents] Failed to play ability sound ${soundPath}:`, err);
        });
      }
    });
  }
};

// Export for use in other modules
// For now, AbilityEvents is a global object loaded via script tag

export { abilityEventsLoggerFallback, abilityEventsLogger, AbilityEvents };
